/**
 * The Charter's docking policy (M5 spec §12.3, §12.5, §14.2, §18 flow 1; seam S-M2-11; plan C14): installed into
 * M2's clearance (`installDockingPolicy`), it mirrors the worker's `law/docking.ts` rules on the main thread from
 * the latest `RelationsView` and the local stations:
 *
 * 1. A station the law doesn't know (not in the local manifest) keeps M2's behaviour: allowed.
 * 2. A **disabled** station always takes the player in for shelter (no scan: nobody runs it).
 *    A station the player **corporation** owns (`stationCorp`) takes its CEO in with no scan and no refusal
 *    (M5 spec §27.3, D-424: the corporation never scans or refuses its own pilot).
 * 3. `hostileStanding`: the mirrored standing with the owner ≤ law.json `standing.hostileAt` (outlaws too).
 * 4. `wanted`: wanted level ≥ 1 with the owner, or with the system's jurisdiction. Outlaw stations ignore bounties.
 * 5. Allowed. At a non-outlaw station, while tetherstone is illegal and in the hold, the **docking scan** finds it:
 *    `onSmuggling(tonnes)` reports the crime and confiscates the cargo; docking goes ahead.
 *
 * Every refusal is kept in `lastRefusal` (faction, wanted level) so C18 can say "Clearance denied: wanted by
 * Tethys (level 1)". Message rate (one call per clearance request).
 */
import lawJson from '../data/strategic/law.json'
import { m2IdOfSite } from '../bridge/m2/stations'
import type { SystemLayout } from '../galaxy/layout/types'
import { CLEARANCE_REASON } from '../space/events'
import { DOCKING_POLICY_REASON, type ClearanceReason, type DockingPolicy } from '../docking/clearance'
import { FACTION_IDS, type RelationsView } from '../strategic/types'
import type { LawWorld } from './crimeDetector'
import { FACTION_OUTLAW } from './hostilityWriter'

export interface DockingRefusal {
  /** The M2 station index refused (−1 = none yet). */
  station: number
  reason: ClearanceReason
  /** The refusing faction index (−1 = none). */
  faction: number
  /** Its wanted level on the player (0 for a hostile standing). */
  level: number
}

export interface DockingPolicyEnv {
  readonly world: LawWorld
  relations(): RelationsView | null
  /** M2's station index → its station def id (null when unknown). */
  stationDefId(index: number): string | null
  tetherstoneIllegal(): boolean
  tetherstoneT(): number
  /** The docking scan found `tonnes` of tetherstone: report `smuggling` and confiscate. */
  onSmuggling(tonnes: number): void
  hostileAt?: number
  /** R3-1 sweep: the live layout, so a home-cluster site (`sys-07-st0`) matches M2's id (`hub`). Absent: slugs only. */
  layout?: () => Pick<SystemLayout, 'stations' | 'home'> | null
}

export interface CharterDockingPolicy extends DockingPolicy {
  readonly lastRefusal: DockingRefusal
}

/** The law world's index of the station M2 calls `defId` (site ids map through `m2IdOfSite` over `layout`), or −1. */
export function lawStationIndex(w: LawWorld, defId: string, layout: Pick<SystemLayout, 'stations' | 'home'> | null = null): number {
  for (let i = 0; i < w.stations; i++) {
    const id = w.stationIds[i]
    if (id === defId || m2IdOfSite(id, layout) === defId) return i
  }
  return -1
}

function wantedOf(view: RelationsView | null, f: number): number {
  if (view === null || f < 0) return 0
  return view.player.wanted[FACTION_IDS[f]] ?? 0
}

function standingOf(view: RelationsView | null, f: number): number {
  if (view === null || f < 0) return 0
  return view.player.standings[FACTION_IDS[f]] ?? 0
}

export function createCharterDockingPolicy(env: DockingPolicyEnv): CharterDockingPolicy {
  const lastRefusal: DockingRefusal = { station: -1, reason: CLEARANCE_REASON.none, faction: -1, level: 0 }
  const refuse = (station: number, reason: ClearanceReason, faction: number, level: number): ClearanceReason => {
    lastRefusal.station = station
    lastRefusal.reason = reason
    lastRefusal.faction = faction
    lastRefusal.level = level
    return reason
  }
  return {
    lastRefusal,
    canDock(station: number): ClearanceReason {
      const w = env.world
      const defId = env.stationDefId(station)
      const i = defId === null ? -1 : lawStationIndex(w, defId, env.layout?.() ?? null)
      if (i < 0) return CLEARANCE_REASON.none
      if (w.stationDisabled[i] === 1 || w.stationCorp[i] === 1) return CLEARANCE_REASON.none
      const view = env.relations()
      const owner = w.stationOwner[i]
      const hostileAt = env.hostileAt ?? lawJson.standing.hostileAt
      if (view !== null && standingOf(view, owner) <= hostileAt) return refuse(station, DOCKING_POLICY_REASON.hostileStanding, owner, 0)
      const outlaw = owner >= 0 && FACTION_OUTLAW[owner] === 1
      if (!outlaw) {
        const lo = wantedOf(view, owner)
        if (lo >= 1) return refuse(station, DOCKING_POLICY_REASON.wanted, owner, lo)
        const j = w.jurisdiction
        const lj = wantedOf(view, j)
        if (lj >= 1) return refuse(station, DOCKING_POLICY_REASON.wanted, j, lj)
        const t = env.tetherstoneT()
        if (t > 0 && env.tetherstoneIllegal()) env.onSmuggling(t)
      }
      return CLEARANCE_REASON.none
    },
  }
}
